import React, { useEffect, useState } from "react";
import { usePageContext } from "../../App";
import TariffCard from "../TariffCard/TariffCard";
import styled from "styled-components";
import {
  Card,
  Checkbox,
  CTAButton,
  Disclosure,
  ErrorNotification,
  Heading3,
  P,
  SelectField,
  SkeletonHeading,
  SkeletonText,
  theme,
  VisuallyHidden,
} from "@ovotech/element";
import { Tariff } from "../../models/Tariff";
import { Fuel } from "../../models/Fuel";
import { GetTariffsRequest } from "../../services/api";
import groupBy from "lodash/groupBy";
import { ProfileClass } from "../../models/ProfileClass";
import { PaymentMethod } from "../../models/PaymentMethod";

interface TariffListProps {
  postcode: string;
  region: string | null;
}

const TariffListHeading = styled(Heading3)`
  color: ${theme.colors.primary};
`;

const SkeletonCard = styled(Card)`
  margin-top: ${theme.space["5"]};
  margin-bottom: ${theme.space["5"]};
`;

const TariffListControls = styled.div`
  padding-left: ${theme.space["4"]};
  padding-right: ${theme.space["4"]};
  margin-top: ${theme.space["10"]};
  margin-bottom: ${theme.space["10"]};
`;

const CentredContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
`;

interface ErrorMessage {
  title: string;
  message: string;
}

interface Filters {
  profileClass?: ProfileClass;
  paymentMethod?: PaymentMethod;
  fuel?: Fuel;
  forSale: boolean;
}

function TariffList({ postcode, region }: TariffListProps) {
  const emptyError = { title: "", message: "" };
  const { apiClient } = usePageContext();
  const [tariffs, setTariffs] = useState<Tariff[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<ErrorMessage>(emptyError);
  const [showErrorBanner, setShowErrorBanner] = useState<boolean>(false);
  const [filters, setFilters] = useState<Filters>({ forSale: true });
  const { setRegion } = usePageContext();

  function setGeneralError(): void {
    setShowErrorBanner(true);
    setErrorMessage({
      title: "Something went wrong",
      message: "We can't get any plans for you at the moment.",
    });
  }

  function setNoTariffsError(): void {
    setShowErrorBanner(true);
    setErrorMessage({
      title: "",
      message:
        "Sorry, there are no plans available for this postcode at the moment.",
    });
  }

  function clearError(): void {
    setShowErrorBanner(false);
    setErrorMessage(emptyError);
  }

  function setFuelFilter(fuel: Fuel): void {
    setFilters({
      ...filters,
      fuel,
    });
  }

  useEffect(() => {
    const abortCtrl = new AbortController();
    const fetchOpts = { signal: abortCtrl.signal };

    setLoading(true);
    clearError();
    setTariffs([]);

    let initialParams;
    if (region === null || region.length === 0) {
      initialParams = { postcode };
    } else {
      initialParams = { region };
    }

    const requestParams: GetTariffsRequest = Object.entries(filters).reduce(
      (result: GetTariffsRequest, [key, value]) => {
        // These values were needed for the UI components but are invalid in
        // the API so leave them out
        if (value !== "All" && value !== 0 && value !== "0") {
          result[key as keyof Filters] = value;
        }
        return result;
      },
      initialParams
    );

    apiClient
      .getTariffs(requestParams, fetchOpts)
      .then((response) => {
        if (response.ok) {
          return response
            .json()
            .then((json) => {
              setRegion(json.region);
              const tariffs = json.tariffs;
              if (tariffs.length > 0) {
                const grouped = tariffs.map((tariff: Tariff) => {
                  const groupedTils = groupBy(
                    tariff.tariffInformationLabels,
                    "activeFrom"
                  );
                  return {
                    ...tariff,
                    groupedTils: groupedTils,
                  };
                });

                setTariffs(grouped);
              } else {
                setNoTariffsError();
              }
            })
            .catch(() => {
              setGeneralError();
            });
        } else {
          setGeneralError();
        }
      })
      .catch((error) => {
        if (error.name !== "AbortError") setGeneralError();
      })
      .finally(() => setLoading(false));
    return () => {
      setLoading(false);
      abortCtrl.abort();
    };
  }, [postcode, filters]);

  return (
    <section>
      <TariffListControls>
        <CentredContainer>
          <TariffListHeading>OVO plans</TariffListHeading>
          <P>
            The list below shows energy plans and prices available in your area.
            You can see plans for different fuel or meter types, plus plans that
            aren’t available at the moment.{" "}
          </P>
          <div>
            <VisuallyHidden>Select fuel type</VisuallyHidden>
            <CTAButton
              disabled={loading}
              variant={filters.fuel === undefined ? "primary" : "secondary"}
              onClick={() => {
                if (filters.fuel) {
                  const updated = { ...filters };
                  delete updated.fuel;
                  setFilters(updated);
                }
              }}
            >
              All
            </CTAButton>
            <CTAButton
              disabled={loading}
              variant={
                filters.fuel === Fuel.ELECTRICITY ? "primary" : "secondary"
              }
              onClick={() =>
                filters.fuel !== Fuel.ELECTRICITY &&
                setFuelFilter(Fuel.ELECTRICITY)
              }
            >
              Electricity
            </CTAButton>
            <CTAButton
              disabled={loading}
              variant={filters.fuel === Fuel.GAS ? "primary" : "secondary"}
              onClick={() =>
                filters.fuel !== Fuel.GAS && setFuelFilter(Fuel.GAS)
              }
            >
              Gas
            </CTAButton>
          </div>
        </CentredContainer>
        <Disclosure title="Filters">
          <Checkbox
            disabled={loading}
            className="pointer"
            label="Available for sale"
            id="includeForSale"
            checked={filters.forSale}
            onChange={() =>
              setFilters({
                ...filters,
                forSale: !filters.forSale,
              })
            }
          />
          <SelectField
            disabled={loading}
            id="meter-type-select"
            label="Meter type"
            value={filters.profileClass}
            onValueChange={(newValue) => {
              newValue &&
                setFilters({
                  ...filters,
                  profileClass: parseInt(newValue),
                });
            }}
            options={[
              { value: "0", label: "All" },
              { value: "1", label: "Standard" },
              { value: "2", label: "Multi rate" },
            ]}
          />
          <SelectField
            disabled={loading}
            id="payment-method-select"
            label="Payment method"
            value={filters.paymentMethod}
            onValueChange={(newValue) => {
              newValue &&
                setFilters({
                  ...filters,
                  paymentMethod: newValue as PaymentMethod,
                });
            }}
            options={[
              { value: "0", label: "All" },
              { value: PaymentMethod.PAYM, label: "Direct Debit" },
              { value: PaymentMethod.PAYG, label: "Pay as you go" },
              { value: PaymentMethod.PAY_NON_DD, label: "On demand" },
            ]}
          />
        </Disclosure>
      </TariffListControls>
      {showErrorBanner ? (
        <ErrorNotification id="general-error" title={errorMessage.title}>
          {errorMessage.message}
        </ErrorNotification>
      ) : null}
      {loading ? (
        <>
          <CentredContainer>
            <P>Loading plans</P>
          </CentredContainer>
          <SkeletonCard>
            <SkeletonHeading />
            <SkeletonText lines={3} />
          </SkeletonCard>
        </>
      ) : (
        <ul>
          {tariffs.map((tariff) => (
            <li key={tariff.planId}>
              {tariff.groupedTils &&
                Object.entries(tariff.groupedTils).map(([activeFrom, tils]) => {
                  return (
                    <TariffCard
                      key={tariff.name + activeFrom + tils.length}
                      name={tariff.name}
                      activeFrom={activeFrom}
                      tils={tils}
                    />
                  );
                })}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

export default TariffList;
